// @flow

import React, {
  useRef,
  useCallback,
  useState,
  useEffect,
  useMemo,
} from "react";
import { useDispatch, useSelector } from "react-redux";

import classNames from "classnames";
import {
  append,
  contains,
  equals,
  findIndex,
  identity,
  includes,
  indexBy,
  isEmpty,
  not,
  pipe,
  pick,
  prop,
  subtract,
  uniq,
  union,
  without,
} from "ramda";

import { Maybe } from "ramda-fantasy";

import Downshift from "downshift";
import { useMount } from "react-use";

import { Translate, useI18n } from "@quantum/i18n";
import { Spinner } from "@quantum/ui";

import { useScrollArea } from "../../../comum/Scroll";

import {
  useBuscaAtivosColecao,
  useBuscaInformacoesAdicionaisAtivos,
} from "../../../remote";

import BuscarAtivosSelecionaveis from "./buscarAtivosSelecionaveis/index.js";
import { tipoGrupoAtivosPermitidos } from "./buscarAtivosSelecionaveis/resultadoAtivos";

import {
  getAtivosSelecionados,
  getAtivosCarteiraSelecionados,
  selecionaAtivosCarteira,
} from "../../../../state";

import "./Ativo.css";

const stateReducer = (state: any, changes: any): any => {
  switch (changes.type) {
    case Downshift.stateChangeTypes.mouseUp:
      return {
        ...changes,
        isOpen: state.isOpen,
        inputValue: state.inputValue,
      };

    case Downshift.stateChangeTypes.keyDownEnter:
      return {
        ...changes,
        inputValue: state.inputValue,
        isOpen: state.isOpen,
        highlightedIndex: state.highlightedIndex,
      };

    case Downshift.stateChangeTypes.clickItem: {
      return {
        ...changes,
        inputValue: state.inputValue,
        isOpen: state.isOpen,
        highlightedIndex: state.highlightedIndex,
      };
    }

    case Downshift.stateChangeTypes.blurInput:
    case Downshift.stateChangeTypes.touchEnd:
    case Downshift.stateChangeTypes.itemMouseEnter:
      return {
        ...changes,
        inputValue: state.inputValue,
      };

    default:
      return changes;
  }
};

const takeOptionsBetween = (start, end, options) => {
  const startIndex = findIndex(equals(start), options);
  const endIndex = findIndex(equals(end), options);
  const [finalStart, finalEnd] = [startIndex, endIndex].sort(subtract);
  return options.slice(finalStart, finalEnd + 1);
};

const tipoAtivosColecao = [
  "GRUPO_ATIVOS",
  "GRUPO_MISTO",
  "BUSCA_AVANCADA_FUNDOS",
];
const tipoAtivosNaoPermitidos = [
  "PGBL_VGBL",
  "RPPS",
  "EFPC",
  "EMPRESA_ABERTA",
  "EMPRESA_AMERICANA",
  "EMPRESA_FECHADA",
];

const Ativo = () => {
  const { translate } = useI18n();

  const inputRef = useRef();
  const holdingShift = useRef(false);
  const lastSelection = useRef(null);
  const pivotSelecion = useRef(null);
  const lastPivotChange = useRef([]);

  const ativosSelecionados = useSelector(getAtivosSelecionados);

  const ativosMarcados = useSelector(getAtivosCarteiraSelecionados);
  const value = ativosMarcados;
  const dispatch = useDispatch();

  const [options, setOptions] = useState(Maybe(ativosMarcados).getOrElse([]));
  const [
    ativosSelecionadosParaBusca,
    setAtivosSelecionadosParaBusca,
  ] = useState([]);

  const [idColecao, setIdColecao] = useState("");
  const [tipoColecao, setTipoColecao] = useState("");

  const abrirGrupos = (identificador, tipoItemSelecionavel) => {
    setIdColecao(identificador);
    setTipoColecao(tipoItemSelecionavel);
  };

  const onSelecionarAtivo = ativo => {
    const { id, tipoItemSelecionavel, nome } = ativo;
    const ativoSelecionavel = { id, tipoItemSelecionavel, nome };

    if (includes(tipoItemSelecionavel, tipoAtivosColecao)) {
      abrirGrupos(id, tipoItemSelecionavel);
    } else if (not(includes(ativoSelecionavel, options))) {
      setOptions([...options, ativoSelecionavel]);
      dispatch(selecionaAtivosCarteira([...ativosMarcados, ativoSelecionavel]));
    }

    focusInput();
  };

  const {
    data: ativosColecao = [],
    loading: loadingBuscaColecao,
    error: errorBuscaColecao,
  } = useBuscaAtivosColecao({
    properties: { identificador: idColecao, tipoItemSelecionavel: tipoColecao },
    fetch: Boolean(idColecao),
  });

  useEffect(() => {
    if (ativosColecao.length) {
      const ativosFiltrados = ativosColecao.filter(({ tipoItemSelecionavel }) =>
        not(includes(tipoItemSelecionavel, tipoAtivosNaoPermitidos))
      );
      setOptions(ativosFiltrados);
      dispatch(selecionaAtivosCarteira(ativosFiltrados));
    }
  }, [dispatch, ativosColecao]);

  const opcoesIndexadas = indexBy(prop("id"), options);

  const onChange = useCallback(
    selecao => {
      const ativos = selecao
        .filter(({ id }) => opcoesIndexadas[id])
        .filter(Boolean);
      dispatch(selecionaAtivosCarteira(ativos));
    },
    [dispatch, opcoesIndexadas]
  );

  const focusInput = useCallback(() => {
    if (inputRef.current) inputRef.current.focus();
  }, [inputRef]);

  const onChangeHandler = useCallback(
    (selection, { inputValue }) => {
      if (!selection) return;

      if (!holdingShift.current) pivotSelecion.current = null;

      if (pivotSelecion.current) {
        const below = takeOptionsBetween(
          pivotSelecion.current,
          selection,
          options
        );
        const above = takeOptionsBetween(selection, pivotSelecion, options);
        const change = [...below, ...above];

        const withoutPrevious = without(lastPivotChange.current);
        const withPivot = append(pivotSelecion.current);

        onChange(pipe(withoutPrevious, union(change), withPivot, uniq)(value));
        lastPivotChange.current = change;
      } else if (contains(selection, value)) {
        onChange(without([selection], value));
        pivotSelecion.current = null;
        lastSelection.current = null;
        lastPivotChange.current = [];
      } else {
        onChange(append(selection, value));
        lastSelection.current = selection;
        lastPivotChange.current = [];
        if (pivotSelecion.current == null && holdingShift.current) {
          pivotSelecion.current = lastSelection.current;
        }
      }
      focusInput();
    },
    [
      value,
      onChange,
      focusInput,
      holdingShift,
      lastSelection,
      lastPivotChange,
      options,
    ]
  );

  const desmarcarAtivos = useCallback(() => {
    dispatch(selecionaAtivosCarteira([]));
  }, [dispatch]);

  const usarAtivosSelecionados = useCallback(() => {
    const ativosBusca = ativosSelecionados.map(
      ({ id: identificador, type: tipoItemSelecionavel }) => ({
        identificador,
        tipoItemSelecionavel,
      })
    );
    setAtivosSelecionadosParaBusca(ativosBusca);
  }, [ativosSelecionados]);

  const {
    data: ativosBuscados = [],
    loading: loadingBuscaAtivos,
    error: errorBuscaAtivos,
  } = useBuscaInformacoesAdicionaisAtivos({
    properties: { ativos: ativosSelecionadosParaBusca },
    fetch: Boolean(not(isEmpty(ativosSelecionadosParaBusca))),
  });

  useEffect(() => {
    if (ativosBuscados.length) {
      const ativosFiltrados = ativosBuscados
        .filter(({ nome }) => Boolean(nome))
        .filter(({ codigoGrupo }) =>
          includes(String(codigoGrupo), tipoGrupoAtivosPermitidos)
        )
        .filter(({ tipoItemSelecionavel }) =>
          not(includes(tipoItemSelecionavel, tipoAtivosNaoPermitidos))
        )
        .map(({ id, nome, tipoItemSelecionavel }) => ({
          id,
          nome,
          tipoItemSelecionavel,
        }));
      setOptions(ativosFiltrados);
      dispatch(selecionaAtivosCarteira(ativosFiltrados));
    }
  }, [dispatch, ativosBuscados]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.shiftKey) {
        holdingShift.current = true;
        pivotSelecion.current = lastSelection.current;
      }
    };

    const onKeyUp = (event: KeyboardEvent) => {
      if (!event.shiftKey) {
        holdingShift.current = false;
      }
    };

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("keyup", onKeyUp);

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("keyup", onKeyUp);
    };
  }, [holdingShift, pivotSelecion, lastSelection]);

  const {
    registerScrollArea,
    registerScrollTarget,
    scrollTo,
  } = useScrollArea();

  useMount(() => {
    if (value.length > 0) {
      const target = options.find(option =>
        value.map(prop("id")).includes(option.id)
      );
      if (target) scrollTo(target.id, { offsetTop: -30 });
    }
  });

  const mensagem = useMemo(() => {
    if (errorBuscaColecao || errorBuscaAtivos) {
      return translate("configuracaoRelatorio.buscaAtivos.nenhumEncontrado");
    } else if (options.length === 0) {
      return translate("configuracaoRelatorio.buscaAtivos.instrucao");
    }
  }, [translate, options, errorBuscaColecao, errorBuscaAtivos]);

  return (
    <div className="ConfiguracaoMedida">
      <div className="ConfiguracaoMedida__label">
        <Translate>Comum.Ativos</Translate>
        <div className="ConfiguracaoMedida__label">
          {ativosMarcados.length >= 1 && (
            <span
              className="ConfiguracaoMedida__label--desmarcar"
              onClick={desmarcarAtivos}
            >
              <Translate>Comum.desmarcarTodos</Translate>
            </span>
          )}
          <span
            className="ConfiguracaoMedida__label--usarSelecionados"
            onClick={usarAtivosSelecionados}
          >
            <Translate>Comum.UsarAtivosSelecionados</Translate>
          </span>
        </div>
      </div>

      <Downshift
        stateReducer={stateReducer}
        onSelect={onChangeHandler}
        itemToString={item => (item ? String(item.id) : "")}
      >
        {({
          getItemProps,
          getMenuProps,
          isOpen,
          inputValue,
          highlightedIndex,
          selectedItem,
        }) => (
          <div
            className={classNames(
              "q-Select",
              "q-Select--multiple",
              "ConfiguracaoAtivosCarteira"
            )}
          >
            <div
              className={classNames("q-SelectBody", "q-SelectBody--multiple")}
            >
              <div className="q-SelectBody__search">
                <BuscarAtivosSelecionaveis
                  setarInputAoSelecionarAtivo={false}
                  inputRef={inputRef}
                  selecionarAtivo={onSelecionarAtivo}
                />
              </div>

              {(loadingBuscaColecao || loadingBuscaAtivos) && <Spinner />}

              {mensagem && !loadingBuscaColecao && !loadingBuscaAtivos && (
                <div className="ConfiguracaoAtivosCarteira__mensagem">
                  {mensagem}
                </div>
              )}

              {!(
                loadingBuscaColecao ||
                errorBuscaColecao ||
                loadingBuscaAtivos ||
                errorBuscaAtivos
              ) && (
                <ol
                  className="q-SelectBody__items"
                  {...getMenuProps({ ref: registerScrollArea })}
                  onScroll={focusInput}
                >
                  {options.map((item, index) => (
                    <li
                      ref={registerScrollTarget(item.id)}
                      key={item.id}
                      className={classNames(
                        "q-SelectItem",
                        "q-SelectItem--multiple",
                        {
                          "q-SelectItem--selected": contains(item, value),
                        }
                      )}
                      style={{
                        backgroundColor:
                          highlightedIndex === index
                            ? "#f3f9ff"
                            : "transparent",
                      }}
                    >
                      <label
                        {...pick(
                          ["onMouseMove"],
                          getItemProps({
                            index,
                            item,
                          })
                        )}
                      >
                        <input
                          type="checkbox"
                          {...getItemProps({
                            index,
                            item,
                            checked: contains(item, value),
                          })}
                          value={item.id}
                          onChange={identity}
                        />
                        {item.nome}
                      </label>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
        )}
      </Downshift>
    </div>
  );
};

export default Ativo;
